import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getDashboard } from '~/api/monitoring'
import type { AdminDashboardDetail } from '~/api/types'
import {
    AlertActions,
    Field,
    IndicatorTable,
    IngestHealth,
    NotifySettings,
    SourceMappings,
} from '~/components/DashboardInsights'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { SidePanelDetail } from '~/components/ui/side-panel-detail'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import { useToast } from '~/components/ui/toast'
import { formatDateTime } from '~/lib/utils'

// 모니터링 대시보드 상세 슬라이드오버. 기본정보 + 측정값 수 + 알림 목록.
const SEVERITY_TONES: Record<string, BadgeTone> = {
    critical: 'danger',
    high: 'danger',
    warning: 'warning',
    medium: 'warning',
    low: 'info',
    info: 'info',
}

const ALERT_STATUS_TONES: Record<string, BadgeTone> = {
    unread: 'warning',
    open: 'warning',
    acknowledged: 'info',
    resolved: 'success',
    closed: 'neutral',
}

export function DashboardDetailPanel({
    dashboardId,
    onClose,
}: {
    dashboardId: string
    onClose: () => void
}) {
    const navigate = useNavigate()
    const toast = useToast()
    const [detail, setDetail] = useState<AdminDashboardDetail | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)

    // 제어 액션 성공 후 상세를 다시 불러온다. 초기 로딩 실패는 패널에 error로 보이지만,
    // 갱신 실패는 기존 표시를 유지한 채 토스트로만 알린다(패널이 통째로 사라지지 않도록).
    const load = useCallback(async () => {
        try {
            setDetail(await getDashboard(dashboardId))
        } catch (e) {
            toast.error((e as Error).message)
        }
    }, [dashboardId, toast])

    useEffect(() => {
        let alive = true
        setLoading(true)
        setError(null)
        getDashboard(dashboardId)
            .then((data) => {
                if (alive) setDetail(data)
            })
            .catch((e) => {
                if (alive) setError(e.message)
            })
            .finally(() => {
                if (alive) setLoading(false)
            })
        return () => {
            alive = false
        }
    }, [dashboardId])

    return (
        <SidePanelDetail
            title='대시보드 상세'
            data={detail}
            loading={loading}
            error={error}
            onClose={onClose}
            headerActions={
                <Button
                    variant='outline'
                    size='sm'
                    onClick={() => {
                        onClose()
                        navigate(`/dashboards/${dashboardId}`)
                    }}>
                    상세보기
                </Button>
            }>
            {(detail) => (
                <>
                    <section>
                        <h3 className='text-foreground mb-2 text-sm font-bold'>
                            기본정보
                        </h3>
                        <dl className='grid grid-cols-2 gap-x-4 gap-y-2 text-sm'>
                            <Field label='이름' value={detail.name} />
                            <Field
                                label='평가 ID'
                                value={detail.assessmentId ?? '-'}
                            />
                            <Field
                                label='알림 이메일'
                                value={detail.notifyEmail ?? '-'}
                            />
                            <Field
                                label='측정값 수'
                                value={detail.readingsCount}
                            />
                            <Field
                                label='생성일'
                                value={formatDateTime(detail.createdAt)}
                            />
                        </dl>
                    </section>

                    <section>
                        <h3 className='text-foreground mb-2 text-sm font-bold'>
                            인제스트 건강도
                        </h3>
                        <IngestHealth detail={detail} />
                    </section>

                    <section>
                        <h3 className='text-foreground mb-2 text-sm font-bold'>
                            통지 설정
                        </h3>
                        <NotifySettings detail={detail} onChanged={load} />
                    </section>

                    <section>
                        <h3 className='text-foreground mb-2 text-sm font-bold'>
                            지표
                        </h3>
                        <IndicatorTable detail={detail} />
                    </section>

                    <section>
                        <h3 className='text-foreground mb-2 text-sm font-bold'>
                            소스 매핑
                        </h3>
                        <SourceMappings detail={detail} />
                    </section>

                    <section>
                        <h3 className='text-foreground mb-2 text-sm font-bold'>
                            알림
                        </h3>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>필드</TableHead>
                                    <TableHead>유형</TableHead>
                                    <TableHead>심각도</TableHead>
                                    <TableHead>상태</TableHead>
                                    <TableHead>제목/항목</TableHead>
                                    <TableHead>생성일</TableHead>
                                    <TableHead>갱신</TableHead>
                                    <TableHead className='text-right'>
                                        조치
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {detail.alerts.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={8}
                                            className='text-muted-foreground py-6 text-center'>
                                            알림이 없어요.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    detail.alerts.map((a) => (
                                        <TableRow key={a.id}>
                                            <TableCell className='font-mono text-xs'>
                                                {a.fieldId ?? '-'}
                                            </TableCell>
                                            <TableCell>
                                                {a.alertType ?? '-'}
                                            </TableCell>
                                            <TableCell>
                                                {a.severity ? (
                                                    <Badge
                                                        tone={
                                                            SEVERITY_TONES[
                                                                a.severity
                                                            ] ?? 'neutral'
                                                        }>
                                                        {a.severity}
                                                    </Badge>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {a.status ? (
                                                    <Badge
                                                        tone={
                                                            ALERT_STATUS_TONES[
                                                                a.status
                                                            ] ?? 'neutral'
                                                        }>
                                                        {a.status}
                                                    </Badge>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            <TableCell
                                                title={a.message ?? undefined}>
                                                <span className='block'>
                                                    {a.title ?? '-'}
                                                </span>
                                                {a.itemTitle ? (
                                                    <span className='text-muted-foreground block text-xs'>
                                                        {a.itemTitle}
                                                    </span>
                                                ) : null}
                                            </TableCell>
                                            <TableCell className='whitespace-nowrap'>
                                                {formatDateTime(a.createdAt)}
                                            </TableCell>
                                            <TableCell className='whitespace-nowrap'>
                                                {a.updatedAt
                                                    ? formatDateTime(
                                                          a.updatedAt
                                                      )
                                                    : '—'}
                                            </TableCell>
                                            <TableCell className='text-right'>
                                                <AlertActions
                                                    dashboardId={detail.id}
                                                    alert={a}
                                                    onChanged={load}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </section>
                </>
            )}
        </SidePanelDetail>
    )
}
